import { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png?url';
import markerIcon from 'leaflet/dist/images/marker-icon.png?url';
import markerShadow from 'leaflet/dist/images/marker-shadow.png?url';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

type PortLocation = {
  name: string;
  state: string;
  coords: [number, number];
};

const portLocations: PortLocation[] = [
  { name: 'Manzanillo', state: 'Colima', coords: [19.0507, -104.3158] },
  { name: 'Lázaro Cárdenas', state: 'Michoacán', coords: [17.9583, -102.1936] },
  { name: 'Veracruz', state: 'Veracruz', coords: [19.2019, -96.1332] },
  { name: 'Altamira', state: 'Tamaulipas', coords: [22.4007, -97.9336] },
  { name: 'Ensenada', state: 'Baja California', coords: [31.8667, -116.6000] },
  { name: 'Guaymas', state: 'Sonora', coords: [27.9217, -110.9030] },
  { name: 'Mazatlán', state: 'Sinaloa', coords: [23.2494, -106.4111] },
  { name: 'Puerto Progreso', state: 'Yucatán', coords: [21.2833, -89.6667] },
  { name: 'Coatzacoalcos', state: 'Veracruz', coords: [18.1333, -94.4500] },
  { name: 'Tuxpan', state: 'Veracruz', coords: [20.9511, -97.4092] },
  { name: 'Salina Cruz', state: 'Oaxaca', coords: [16.1699, -95.2010] },
  { name: 'Puerto Vallarta', state: 'Jalisco', coords: [20.6534, -105.2253] },
];

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const [selectedPort, setSelectedPort] = useState<string | null>(null);
  const headerIsActive = isScrolled || mobileMenuOpen;
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) return;

    const map = L.map(mapContainerRef.current, {
      zoomControl: false,
    });
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '© OpenStreetMap, © CartoDB',
    }).addTo(map);

    const bounds = L.latLngBounds([]);

    portLocations.forEach((port) => {
      const marker = L.marker(port.coords).addTo(map).bindPopup(`<strong>${port.name}</strong><br />${port.state}`);
      markersRef.current.set(port.name, marker);
      bounds.extend(port.coords);
    });

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40] });
    } else {
      map.setView([23.6345, -102.5528], 5);
    }

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current.clear();
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handlePortClick = (port: PortLocation) => {
    setSelectedPort(port.name);
    const map = mapInstanceRef.current;
    const marker = markersRef.current.get(port.name);
    if (map) {
      map.flyTo(port.coords, 6, { duration: 0.6 });
    }
    if (marker) {
      marker.openPopup();
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Web3Forms required fields
    formData.set('access_key', 'f3c0a63b-4934-45b0-9ce3-df51126f7866');
    formData.set('subject', 'Nueva cotización desde T600');
    formData.set('from_name', 'Formulario Web T600');

    // Validaciones básicas (las tuyas)
    if (
      !formData.get('nombre') ||
      !formData.get('empresa') ||
      !formData.get('telefono') ||
      !formData.get('email') ||
      !formData.get('servicio')
    ) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        alert('¡Gracias! Tu solicitud fue enviada correctamente.');
        form.reset();
        setMessageLength(0);
      } else {
        alert('Error al enviar el formulario. Intenta nuevamente.');
        console.error(result);
      }
    } catch (error) {
      alert('Error de conexión. Intenta más tarde.');
      console.error(error);
    }
  };



  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed w-full top-0 z-[5000] transition-all duration-300 ${headerIsActive ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('hero')}>
              <img
                alt="T600 Logo"
                className="h-12 w-auto"
                src={headerIsActive ? '/images/logos/logo-t600-black.png' : '/images/logos/logo-t600-white.png'}
              />
            </div>

            <div className="hidden lg:flex items-center space-x-8">
              <button onClick={() => scrollToSection('hero')} className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-200'}`}>
                Inicio
              </button>
              <button onClick={() => scrollToSection('services')} className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-200'}`}>
                Servicios
              </button>
              <button onClick={() => scrollToSection('fleet')} className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-200'}`}>
                Nuestra Flota
              </button>
              <button onClick={() => scrollToSection('security')} className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-200'}`}>
                Seguridad
              </button>
              <button onClick={() => scrollToSection('puertos')} className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-200'}`}>
                Cobertura
              </button>
              <button onClick={() => scrollToSection('contact')} className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-200'}`}>
                Contacto
              </button>
            </div>
            <button
              onClick={() => scrollToSection('contact')}
              className="
                hidden lg:block
                bg-brand text-white
                px-6 py-3 rounded-full
                font-medium
                hover:bg-brand-hover
                active:bg-brand-active
                transition-all whitespace-nowrap cursor-pointer
              "
            >
              Cotizar Ahora
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden cursor-pointer">
              <i className={`text-2xl ${headerIsActive ? 'text-gray-700' : 'text-white'} ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white shadow-lg">
            <div className="px-6 py-4 space-y-4">
              <button onClick={() => scrollToSection('hero')} className="block w-full text-left font-medium text-gray-700 hover:text-blue-600 cursor-pointer">
                Inicio
              </button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left font-medium text-gray-700 hover:text-blue-600 cursor-pointer">
                Servicios
              </button>
              <button onClick={() => scrollToSection('fleet')} className="block w-full text-left font-medium text-gray-700 hover:text-blue-600 cursor-pointer">
                Nuestra Flota
              </button>
              <button onClick={() => scrollToSection('security')} className="block w-full text-left font-medium text-gray-700 hover:text-blue-600 cursor-pointer">
                Seguridad
              </button>
              <button onClick={() => scrollToSection('puertos')} className="block w-full text-left font-medium text-gray-700 hover:text-blue-600 cursor-pointer">
                Cobertura
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left font-medium text-gray-700 hover:text-blue-600 cursor-pointer">
                Contacto
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="
                  w-full
                  bg-brand text-white
                  px-6 py-3 rounded-full
                  font-medium
                  hover:bg-brand-hover
                  active:bg-brand-active
                  transition-all whitespace-nowrap cursor-pointer
                "
              >
                Cotizar Ahora
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            alt="T600 Transporte Portuario"
            className="w-full h-full object-cover object-center"
            src="/images/services/barco.avif"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-left w-full">
          <div className="max-w-3xl">
            <div className="hidden sm:inline-block border-2 border-white/80 px-4 py-2 rounded-full mb-6">
              <span className="text-white text-sm font-medium">Transporte Portuario Profesional</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Movimiento seguro desde los puertos de México
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-10 font-light">
              Manzanillo • Veracruz • Altamira • Lázaro Cárdenas
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => scrollToSection('contact')}
              className="
                bg-brand text-white
                px-8 py-4 rounded-full
                font-semibold text-lg
                hover:bg-brand-hover
                active:bg-brand-active
                transition-all shadow-xl hover:shadow-2xl
                whitespace-nowrap cursor-pointer
              "
            >
              Solicitar Cotización
            </button>
              <button onClick={() => scrollToSection('services')} className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer">
                Ver Servicios
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#1A2B4A] mb-12 leading-tight">
                Experiencia que mueve tu carga con puntualidad
              </h2>
              <div className="grid grid-cols-3 gap-8">
                <div className="text-center border-r border-gray-200 last:border-r-0">
                  <div className="text-stat text-blue-600 mb-2">15+</div>
                  <div className="text-gray-600 text-sm">Años de experiencia</div>
                </div>
                <div className="text-center border-r border-gray-200 last:border-r-0">
                  <div className="text-stat text-blue-600 mb-2">4</div>
                  <div className="text-gray-600 text-sm">Puertos principales</div>
                </div>
                <div className="text-center">
                  <div className="text-stat text-blue-600 mb-2">500+</div>
                  <div className="text-gray-600 text-sm">Entregas mensuales</div>
                </div>
              </div>
            </div>
            <div className="relative h-96 rounded-3xl overflow-hidden">
              <img
                src="/images/services/socios.avif"
                alt="Imagen"
                className="
                  w-full h-full object-cover
                  object-[calc(50%-70px)_center]
                  lg:object-center
                "
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A2B4A]">
              Servicios especializados en logística portuaria
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Ofrecemos soluciones integrales de transporte terrestre desde los principales puertos de México, con tecnología de rastreo y personal altamente capacitado para garantizar la seguridad de tu carga.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
            <div className="lg:col-span-4 bg-[#1A2B4A] rounded-3xl p-8 flex flex-col justify-between overflow-hidden relative h-[500px]">
              <div className="relative z-10">
                <h3 className="text-3xl font-bold text-white mb-4">Arrastre de Contenedores</h3>
                <p className="text-gray-300 text-lg mb-6">20', 40' y 40' HC desde puertos principales</p>
              </div>
              <div className="relative z-10 mt-auto">
                <div className="w-full h-48 mb-6 rounded-2xl overflow-hidden">
                  <img
                    alt="Arrastre de Contenedores"
                    className="w-full h-full object-cover object-center"
                    src="/images/services/camion.avif"
                  />
                </div>
                <button onClick={() => scrollToSection('contact')} className="border-2 border-white text-white px-6 py-3 rounded-full font-medium hover:bg-white hover:text-[#1A2B4A] transition-all whitespace-nowrap cursor-pointer">
                  Más información
                </button>
              </div>
            </div>

            <div className="lg:col-span-3 bg-white rounded-3xl p-8 flex flex-col h-[500px]">
              <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full mb-6 self-start">
                <i className="ri-temp-cold-line text-blue-600"></i>
                <span className="text-sm font-medium text-blue-600">Temperatura Controlada</span>
              </div>
              <div className="w-full h-48 mb-6 rounded-2xl overflow-hidden">
                <img
                  alt="Transporte Refrigerado"
                  className="w-full h-full object-cover object-center"
                  src="/images/services/refrigerado.avif"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Transporte Refrigerado</h3>
              <p className="text-gray-600 mb-6 flex-grow">
                Productos perecederos con monitoreo constante de temperatura
              </p>
              <button onClick={() => scrollToSection('contact')} className="bg-[#1A2B4A] text-white px-6 py-3 rounded-full font-medium hover:bg-[#0f1a2e] transition-all whitespace-nowrap cursor-pointer">
                Cotizar
              </button>
            </div>

            <div className="lg:col-span-3 bg-white rounded-3xl p-8 flex flex-col h-[500px]">
              <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full mb-6 self-start">
                <i className="ri-box-3-line text-gray-700"></i>
                <span className="text-sm font-medium text-gray-700">Carga General</span>
              </div>
              <div className="w-full h-48 mb-6 rounded-2xl overflow-hidden">
                <img
                  alt="Caja Seca"
                  className="w-full h-full object-cover object-center"
                  src="/images/services/caja-seca.avif"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Caja Seca</h3>
              <p className="text-gray-600 mb-6 flex-grow">
                Mercancía general con seguridad garantizada y protección total
              </p>
              <button onClick={() => scrollToSection('contact')} className="bg-[#1A2B4A] text-white px-6 py-3 rounded-full font-medium hover:bg-[#0f1a2e] transition-all whitespace-nowrap cursor-pointer">
                Cotizar
              </button>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Servicio Puerta a Puerta</h3>
              <p className="text-gray-600">
                Recogemos tu carga en el puerto y la entregamos directamente en tu destino final, sin intermediarios.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Entregas Programadas</h3>
              <p className="text-gray-600">
                Coordinamos horarios de entrega según tus necesidades operativas y ventanas de recepción.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-3xl overflow-hidden mb-12 h-96">
            <img
              alt="Flota T600"
              className="w-full h-full object-cover object-center"
              src="/images/services/camiones.avif"
            />
          </div>
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight max-w-4xl mx-auto">
              Transportamos tu carga con <span className="italic text-gray-500">seguridad</span> y{' '}
              <span className="italic text-gray-500">puntualidad</span> garantizadas
            </h2>
            <button
              onClick={() => scrollToSection('contact')}
              className="
                bg-brand text-white
                px-10 py-4 rounded-full
                text-lg font-semibold
                hover:bg-brand-hover
                active:bg-brand-active
                transition-all shadow-lg hover:shadow-xl
                whitespace-nowrap cursor-pointer
              "
            >
              Solicitar Cotización Ahora
            </button>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full mb-4">
              <i className="ri-shield-check-line text-blue-600"></i>
              <span className="text-sm font-medium text-blue-600">Tecnología y Protección</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A2B4A]">
              Monitoreo y seguridad en cada kilómetro
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-[#E8EEF2] rounded-2xl p-8 relative">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-satellite-line text-2xl text-blue-600"></i>
                <span className="text-sm font-semibold text-blue-600">GPS 24/7</span>
              </div>
              <div className="text-6xl text-gray-300 mb-4">"</div>
              <p className="text-gray-700 text-lg leading-relaxed">
                Rastreo satelital en tiempo real de todas nuestras unidades con reportes cada 5 minutos
              </p>
            </div>

            <div className="bg-[#E8EEF2] rounded-2xl p-8 relative">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-award-line text-2xl text-blue-600"></i>
                <span className="text-sm font-semibold text-blue-600">Certificación</span>
              </div>
              <div className="text-6xl text-gray-300 mb-4">"</div>
              <p className="text-gray-700 text-lg leading-relaxed">
                Choferes certificados y capacitados continuamente en protocolos de seguridad vial
              </p>
            </div>

            <div className="rounded-2xl overflow-hidden relative h-80">
              <img
                alt="Chofer Profesional T600"
                className="w-full h-full object-cover object-center"
                src="/images/services/chofer.avif"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white font-semibold text-lg">Carlos Mendoza</p>
                <p className="text-gray-300 text-sm">Operador Certificado • 12 años de experiencia</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 rounded-2xl overflow-hidden relative h-80">
              <img
                alt="Tecnología de Punta"
                className="w-full h-full object-cover object-center"
                src="/images/services/cabina.avif"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1A2B4A]/90 to-transparent p-8">
                <h3 className="text-white font-bold text-2xl mb-2">Tecnología de punta</h3>
                <p className="text-gray-200 mb-2">Sistemas integrados de monitoreo</p>
                <p className="text-gray-300 text-sm">
                  Protocolos de seguridad activos en cada viaje con alertas automáticas
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-[#E8EEF2] rounded-2xl p-6">
                <div className="text-4xl text-gray-300 mb-3">"</div>
                <p className="text-gray-700 mb-4">Seguro de carga incluido con cobertura amplia</p>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-shield-check-line"></i>
                  <span>Protección total</span>
                </div>
              </div>

              <div className="bg-[#E8EEF2] rounded-2xl p-6">
                <div className="text-4xl text-gray-300 mb-3">"</div>
                <p className="text-gray-700 mb-4">Protocolos de reacción inmediata ante contingencias</p>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-alarm-warning-line"></i>
                  <span>Respuesta 24/7</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ports Section */}
      <section id="puertos" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 bg-[#E63946] rounded-full"></div>
              <span className="text-[#E63946] font-semibold text-sm tracking-wider uppercase">
                Cobertura Nacional
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight">
              Puertos y Cobertura
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Conectamos los principales puertos de México con rutas optimizadas y servicio garantizado
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[600px]">
              <div ref={mapContainerRef} className="absolute inset-0 w-full h-full"></div>
            </div>

            <div>
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#E63946] rounded-xl flex items-center justify-center">
                    <i className="ri-ship-line text-3xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-4xl font-black text-gray-900">12</div>
                    <div className="text-gray-600 font-medium">Puertos Conectados</div>
                  </div>
                </div>
                <p className="text-gray-600">
                  Cobertura estratégica en ambas costas del país para garantizar tiempos de respuesta óptimos
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-xl p-8 max-h-[440px] overflow-y-auto">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Puertos Activos</h3>
                <div className="space-y-4">
                  {portLocations.map((port, index) => (
                    <div
                      key={index}
                      onClick={() => handlePortClick(port)}
                      className={`flex items-center justify-between p-4 rounded-xl transition-colors duration-300 cursor-pointer group ${
                        selectedPort === port.name ? 'bg-white border border-[#E63946]/40' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center group-hover:bg-[#E63946] transition-colors duration-300">
                          <i className="ri-map-pin-fill text-[#E63946] group-hover:text-white transition-colors duration-300"></i>
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">{port.name}</div>
                          <div className="text-sm text-gray-600">{port.state}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-green-600">Activo</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section id="fleet" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A2B4A]">Nuestra Flota Especializada</h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Contamos con una flota moderna y diversificada para atender cualquier necesidad de transporte. Todas
              nuestras unidades cuentan con mantenimiento preventivo y tecnología GPS integrada.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <img
                  alt="Plataforma para Contenedores"
                  className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-300"
                  src="/images/services/plataforma.avif"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Plataforma</h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                    <span className="text-gray-600">Capacidad: 20', 40' y 40' HC</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                    <span className="text-gray-600">Peso máximo: 30 toneladas</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                    <span className="text-gray-600">Sistema de sujeción certificado</span>
                  </li>
                </ul>
                <div className="flex gap-2">
                  <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    Mantenimiento Preventivo
                  </span>
                  <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    GPS Integrado
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <img
                  alt="Caja Seca"
                  className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-300"
                  src="/images/services/caja-seca.avif"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Caja Seca</h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                    <span className="text-gray-600">Capacidad: 53 pies</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                    <span className="text-gray-600">Protección total contra intemperie</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                    <span className="text-gray-600">Sistema de seguridad avanzado</span>
                  </li>
                </ul>
                <div className="flex gap-2">
                  <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    Mantenimiento Preventivo
                  </span>
                  <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    GPS Integrado
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <img
                  alt="Caja Refrigerada"
                  className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-300"
                  src="/images/services/caja-refrigerada.avif"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Caja Refrigerada</h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                    <span className="text-gray-600">Rango: -20°C a +20°C</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                    <span className="text-gray-600">Monitoreo de temperatura 24/7</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                    <span className="text-gray-600">Certificación sanitaria vigente</span>
                  </li>
                </ul>
                <div className="flex gap-2">
                  <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    Mantenimiento Preventivo
                  </span>
                  <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    GPS Integrado
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-4xl md:text-5xl font-bold text-[#1A2B4A] mb-6">Solicita tu cotización</h2>
              <p className="text-gray-600 text-lg mb-8">
                Completa el formulario y uno de nuestros ejecutivos se pondrá en contacto contigo en menos de 24
                horas para ofrecerte la mejor solución de transporte.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-blue-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Teléfono</h3>
                    <p className="text-gray-600">+52 (56) 1080-9313</p>
                    <p className="text-gray-600">+52 1 229 552 6033</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-blue-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600">contacto@t600.com.mx</p>
                    <p className="text-gray-600">ventas@t600.com.mx</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl text-blue-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Horario</h3>
                    <p className="text-gray-600">Lunes a Viernes: 8:00 - 18:00</p>
                    <p className="text-gray-600">Sábados: 9:00 - 14:00</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex gap-4">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
              </div>
            </div>

            <div className="lg:col-span-3">
              <form
                id="cotizacion-form"
                data-readdy-form
                onSubmit={handleFormSubmit}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre completo *
                    </label>
                    <input
                      id="nombre"
                      name="nombre"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="Juan Pérez"
                    />
                  </div>
                  <div>
                    <label htmlFor="empresa" className="block text-sm font-medium text-gray-700 mb-2">
                      Empresa *
                    </label>
                    <input
                      id="empresa"
                      name="empresa"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="Nombre de tu empresa"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono *
                    </label>
                    <input
                      id="telefono"
                      name="telefono"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="+52 33 1234 5678"
                      type="tel"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="correo@empresa.com"
                      type="email"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="servicio" className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de servicio requerido *
                  </label>
                  <select
                    id="servicio"
                    name="servicio"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Selecciona un servicio</option>
                    <option value="Arrastre de Contenedores">Arrastre de Contenedores</option>
                    <option value="Transporte Refrigerado">Transporte Refrigerado</option>
                    <option value="Caja Seca">Caja Seca</option>
                    <option value="Servicio Puerta a Puerta">Servicio Puerta a Puerta</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="mensaje" className="block text-sm font-medium text-gray-700 mb-2">
                    Mensaje
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setMessageLength(e.target.value.length)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all resize-none text-sm"
                    placeholder="Cuéntanos más sobre tus necesidades de transporte..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{messageLength}/500 caracteres</p>
                </div>

                <button
                  type="submit"
                  className="
                    w-full
                    bg-brand text-white
                    py-4 rounded-lg
                    font-semibold text-lg
                    hover:bg-brand-hover
                    active:bg-brand-active
                    transition-all
                    disabled:bg-brand-soft
                    disabled:text-brand
                    disabled:cursor-not-allowed
                    whitespace-nowrap
                    cursor-pointer
                  "
                >
                  Enviar Solicitud
                </button>


                <p className="text-xs text-gray-500 mt-4 text-center">
                  Al enviar este formulario, aceptas nuestra{' '}
                  <a href="#" className="text-blue-600 hover:underline">
                    política de privacidad
                  </a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div>
              <img
                alt="T600 Logo"
                className="h-12 w-auto mb-4 brightness-0 invert"
                src="/images/logos/logo-t600-white.png"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                Transporte terrestre especializado desde los principales puertos de México. Seguridad, puntualidad y
                confianza en cada entrega.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-300">SERVICIOS</h3>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Arrastre de Contenedores
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Transporte Refrigerado
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Caja Seca
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('fleet')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Nuestra Flota
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-300">EMPRESA</h3>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => scrollToSection('hero')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Nosotros
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('security')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Seguridad
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('puertos')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Cobertura
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                    Contacto
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-300">SÍGUENOS</h3>
              <div className="flex gap-4">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer"
                >
                  <i className="ri-facebook-fill"></i>
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-line"></i>
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer"
                >
                  <i className="ri-linkedin-fill"></i>
                </a>
              </div>
            </div>
          </div>


          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">© 2024 T600 Transporte. Todos los derechos reservados.</p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">
                Política de Privacidad
              </a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">
                Términos y Condiciones
              </a>
              <a
                href="https://psx.com.mx/?origin=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Powered by PSX
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Button */}
      <a
        href="https://wa.me/5212281197342"
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed bottom-6 right-6 w-16 h-16 bg-[#25D366] rounded-full ${headerIsActive ? 'flex' : 'hidden'} md:flex items-center justify-center shadow-2xl hover:scale-110 transition-transform z-50 cursor-pointer animate-pulse hover:animate-none`}
        aria-label="Contactar por WhatsApp"
      >
        <i className="ri-whatsapp-line text-3xl text-white"></i>
      </a>
    </div>
  );
}
